import type {
  PlannerServiceSpan,
  PlannerTransportVehicle,
} from '@travel-planner/shared/utils/planner-day-services';
import type {
  PlannerTransferKind,
  PlannerTransferSpot,
} from '@travel-planner/shared/utils/planner-transfers';
import type {
  AdvisorDraft,
  AdvisorDraftItem,
  AdvisorGuests,
  AdvisorHotelBooking,
  AdvisorProduct,
  AdvisorRegion,
} from './types';
import type { DaySuggestions } from './v3-suggestions';
import { create } from 'zustand';
import {
  addEmptyDay,
  addProductToDay,
  addProductToDraft,
  addProductToNewDay,
  createEmptyDraft,
  moveItemToDay,
  removeBaseHotel,
  removeDay,
  removeDayService,
  removeItem,
  removeLeg,
  removeTransferLine,
  reorderDayItems,
  restoreDayService,
  setBaseHotel,
  setDayTransportSpan,
  setDayTransportVehicles,
  setHotelBooking,
  setItemComment,
  setItemTime,
  setProductGuests,
  setTransferPlace,
  setTransferRemoved,
  setTransferVehicles,
} from './add-item';
import { addRegionChapter, assignChapterRegions } from './chapter-regions';
import { withDepartureDay } from './departure-day';
import { fillItemDetails } from './item-details';
import {
  mergeGeneratedDraft,
  removeSuggestedFromDraft,
} from './merge-generate';
import {
  acceptDaySuggestion,
  buildDaySuggestions,
  dismissOnDay,
  withoutDay,
  withoutSuggestion,
} from './v3-suggestions';

type AdvisorPlannerStore = {
  draft: AdvisorDraft;
  /**
   * The trip ends on a departure day after its last night (Mini Planner v3);
   * off for layouts without one.
   */
  departureDay: boolean;
  /** Turns the departure day on (or off) and settles the draft. */
  setDepartureDay: (on: boolean) => void;
  /** Per-day Generate picks waiting to be accepted or skipped; not saved. */
  suggestions: DaySuggestions;
  specOpen: boolean;
  proposalOpen: boolean;
  addProduct: (product: AdvisorProduct) => void;
  addProductToDay: (product: AdvisorProduct, dayId: string) => void;
  addProductToNewDay: (product: AdvisorProduct) => void;
  /** A chapter for one of the country's regions, with `days` empty days. */
  addRegion: (
    region: AdvisorRegion,
    country: string | null,
    days?: number
  ) => void;
  /** Moves chapters not tied to a region onto the region holding them. */
  assignRegions: (regions: readonly AdvisorRegion[], country: string) => void;
  addDay: (legId: string) => void;
  /** Drops the day with its items and waiting suggestions. */
  removeDay: (dayId: string) => void;
  removeLeg: (legId: string) => void;
  removeItem: (itemId: string) => void;
  /** The agent's comment on an item for the DMC (XT-834). */
  setItemComment: (itemId: string, comment: string) => void;
  /** A base hotel's Send me a quote / Already booked (T-357). */
  setHotelBooking: (itemId: string, booking: AdvisorHotelBooking) => void;
  /** The item's time on its day; null end: its usual length. */
  setItemTime: (
    itemId: string,
    startTime: string,
    endTime: string | null
  ) => void;
  /** The travellers booked on a product's items; null for everyone (XT-840). */
  setProductGuests: (productId: string, guests: AdvisorGuests | null) => void;
  /** Drops a day's Guide or Transport line (XT-828). */
  removeDayService: (
    dayId: string,
    service: { kind: 'guide' | 'transport'; itemId: string; vehicle?: string }
  ) => void;
  /** Brings back a Guide or Transport the agent removed. */
  restoreDayService: (
    dayId: string,
    service: { kind: 'guide' | 'transport'; itemId: string }
  ) => void;
  /** Brings back one of the trip's transfers (XT-920). */
  restoreTransfer: (dayId: string, kind: PlannerTransferKind) => void;
  /** Drops one chosen car of a transfer, else the transfer. */
  removeTransferLine: (
    dayId: string,
    kind: PlannerTransferKind,
    vehicle?: string
  ) => void;
  /** Where the arrival starts or the departure ends; null: the airport. */
  setTransferPlace: (
    dayId: string,
    kind: 'arrival' | 'departure',
    place: string | PlannerTransferSpot | null
  ) => void;
  /** The cars, or a flight or train, on a transfer; null: the suggested vehicle. */
  setTransferVehicles: (
    dayId: string,
    kind: PlannerTransferKind,
    vehicles: PlannerTransportVehicle[] | null
  ) => void;
  /** The cars on a day's Transport; null: the suggested vehicle. */
  setDayTransportVehicles: (
    dayId: string,
    vehicles: PlannerTransportVehicle[] | null
  ) => void;
  /** The agent's half or full day for a day's Transport (T-326). */
  setDayTransportSpan: (dayId: string, span: PlannerServiceSpan) => void;
  reorderItems: (activeItemId: string, overItemId: string) => void;
  moveItemToDay: (itemId: string, dayId: string) => void;
  /** `nights`: the chapter's day count after (see `setBaseHotel`). */
  setBaseHotel: (product: AdvisorProduct, nights?: number) => void;
  removeBaseHotel: (legId: string) => void;
  setTitle: (title: string) => void;
  setSpecOpen: (open: boolean) => void;
  setProposalOpen: (open: boolean) => void;
  replaceDraft: (draft: AdvisorDraft) => void;
  mergeGenerated: (
    incoming: AdvisorDraft,
    legId: string | null,
    dayId?: string | null
  ) => void;
  removeSuggested: (legId: string | null) => void;
  /**
   * A Generate result: day suggestions when asked for, else merged into the
   * draft.
   */
  applyGenerated: (
    incoming: AdvisorDraft,
    target: {
      legId: string | null;
      dayId: string | null;
      suggest?: boolean;
      /** Catalogue rows to top up thin sold-history picks (region + brief). */
      catalogProducts?: readonly AdvisorProduct[];
      briefMoods?: readonly string[];
    }
  ) => void;
  acceptSuggestion: (dayId: string, item: AdvisorDraftItem) => void;
  /** Drops a pick and records it as dismissed on that day. */
  skipSuggestion: (dayId: string, itemId: string) => void;
  /** Clears the day's picks, recording each as dismissed on that day. */
  dismissSuggestions: (dayId: string) => void;
  /** Writes looked-up catalogue rows into the Generate items asked for. */
  fillItemDetails: (
    requested: readonly string[],
    products: readonly AdvisorProduct[]
  ) => void;
  /** Puts the draft and the waiting suggestions back (Undo). */
  restore: (draft: AdvisorDraft, suggestions: DaySuggestions) => void;
  reset: () => void;
};

type StoreUpdate =
  | Partial<AdvisorPlannerStore>
  | ((state: AdvisorPlannerStore) => Partial<AdvisorPlannerStore>);

export const useAdvisorPlannerStore = create<AdvisorPlannerStore>(
  (setState) => {
    // Every draft change settles the departure day when it is on.
    const set = (update: StoreUpdate) =>
      setState((state) => {
        const next = typeof update === 'function' ? update(state) : update;
        return next.draft && (next.departureDay ?? state.departureDay)
          ? { ...next, draft: withDepartureDay(next.draft) }
          : next;
      });
    return {
      draft: createEmptyDraft(),
      departureDay: false,
      setDepartureDay: (departureDay) =>
        set((state) => ({ departureDay, draft: state.draft })),
      suggestions: {},
      specOpen: false,
      proposalOpen: false,
      addProduct: (product) =>
        set((state) => ({ draft: addProductToDraft(state.draft, product) })),
      addProductToDay: (product, dayId) =>
        set((state) => ({
          draft: addProductToDay(state.draft, product, dayId),
        })),
      addProductToNewDay: (product) =>
        set((state) => ({ draft: addProductToNewDay(state.draft, product) })),
      addRegion: (region, country, days) =>
        set((state) => ({
          draft: addRegionChapter(state.draft, region, country, days),
        })),
      assignRegions: (regions, country) =>
        set((state) => {
          const draft = assignChapterRegions(state.draft, regions, country);
          return draft === state.draft ? state : { draft };
        }),
      addDay: (legId) =>
        set((state) => ({ draft: addEmptyDay(state.draft, legId) })),
      removeDay: (dayId) =>
        set((state) => ({
          draft: removeDay(state.draft, dayId),
          suggestions: withoutDay(state.suggestions, dayId),
        })),
      removeLeg: (legId) =>
        set((state) => ({ draft: removeLeg(state.draft, legId) })),
      removeItem: (itemId) =>
        set((state) => ({ draft: removeItem(state.draft, itemId) })),
      setItemComment: (itemId, comment) =>
        set((state) => ({
          draft: setItemComment(state.draft, itemId, comment),
        })),
      setHotelBooking: (itemId, booking) =>
        set((state) => ({
          draft: setHotelBooking(state.draft, itemId, booking),
        })),
      setItemTime: (itemId, startTime, endTime) =>
        set((state) => ({
          draft: setItemTime(state.draft, itemId, startTime, endTime),
        })),
      setProductGuests: (productId, guests) =>
        set((state) => ({
          draft: setProductGuests(state.draft, productId, guests),
        })),
      removeDayService: (dayId, service) =>
        set((state) => ({
          draft: removeDayService(state.draft, dayId, service),
        })),
      restoreDayService: (dayId, service) =>
        set((state) => ({
          draft: restoreDayService(state.draft, dayId, service),
        })),
      restoreTransfer: (dayId, kind) =>
        set((state) => ({
          draft: setTransferRemoved(state.draft, dayId, kind, false),
        })),
      removeTransferLine: (dayId, kind, vehicle) =>
        set((state) => ({
          draft: removeTransferLine(state.draft, dayId, kind, vehicle),
        })),
      setTransferPlace: (dayId, kind, place) =>
        set((state) => ({
          draft: setTransferPlace(state.draft, dayId, kind, place),
        })),
      setTransferVehicles: (dayId, kind, vehicles) =>
        set((state) => ({
          draft: setTransferVehicles(state.draft, dayId, kind, vehicles),
        })),
      setDayTransportVehicles: (dayId, vehicles) =>
        set((state) => ({
          draft: setDayTransportVehicles(state.draft, dayId, vehicles),
        })),
      setDayTransportSpan: (dayId, span) =>
        set((state) => ({
          draft: setDayTransportSpan(state.draft, dayId, span),
        })),
      reorderItems: (activeItemId, overItemId) =>
        set((state) => ({
          draft: reorderDayItems(state.draft, activeItemId, overItemId),
        })),
      moveItemToDay: (itemId, dayId) =>
        set((state) => ({ draft: moveItemToDay(state.draft, itemId, dayId) })),
      setBaseHotel: (product, nights) =>
        set((state) => ({ draft: setBaseHotel(state.draft, product, nights) })),
      removeBaseHotel: (legId) =>
        set((state) => ({ draft: removeBaseHotel(state.draft, legId) })),
      setTitle: (title) =>
        set((state) => ({ draft: { ...state.draft, title } })),
      setSpecOpen: (specOpen) => set({ specOpen }),
      setProposalOpen: (proposalOpen) => set({ proposalOpen }),
      replaceDraft: (draft) => set({ draft }),
      mergeGenerated: (incoming, legId, dayId = null) =>
        set((state) => ({
          draft: mergeGeneratedDraft(state.draft, incoming, legId, dayId),
        })),
      removeSuggested: (legId) =>
        set((state) => ({
          draft: removeSuggestedFromDraft(state.draft, legId),
        })),
      applyGenerated: (
        incoming,
        { legId, dayId, suggest, catalogProducts = [], briefMoods = [] }
      ) =>
        set((state) =>
          suggest && legId && dayId
            ? {
                suggestions: {
                  ...state.suggestions,
                  [dayId]: buildDaySuggestions(
                    state.draft,
                    incoming,
                    legId,
                    dayId,
                    catalogProducts,
                    briefMoods,
                    state.suggestions
                  ),
                },
              }
            : {
                draft: mergeGeneratedDraft(state.draft, incoming, legId, dayId),
              }
        ),
      acceptSuggestion: (dayId, item) =>
        set((state) => ({
          draft: acceptDaySuggestion(state.draft, dayId, item),
          suggestions: withoutSuggestion(state.suggestions, dayId, item.id),
        })),
      skipSuggestion: (dayId, itemId) =>
        set((state) => {
          const skipped = state.suggestions[dayId]?.find(
            (item) => item.id === itemId
          );
          return {
            draft: skipped
              ? dismissOnDay(state.draft, dayId, [skipped.productId])
              : state.draft,
            suggestions: withoutSuggestion(state.suggestions, dayId, itemId),
          };
        }),
      dismissSuggestions: (dayId) =>
        set((state) => ({
          draft: dismissOnDay(
            state.draft,
            dayId,
            (state.suggestions[dayId] ?? []).map((item) => item.productId)
          ),
          suggestions: withoutDay(state.suggestions, dayId),
        })),
      fillItemDetails: (requested, products) =>
        set((state) => {
          const draft = fillItemDetails(state.draft, requested, products);
          return draft === state.draft ? state : { draft };
        }),
      restore: (draft, suggestions) => set({ draft, suggestions }),
      reset: () =>
        set({
          draft: createEmptyDraft(),
          suggestions: {},
          specOpen: false,
          proposalOpen: false,
        }),
    };
  }
);
